/* eslint-disable import/order */

/**
 * These interfaces and types are not extensive but are being added to incrementaly
 * as they're being implemented throughout the wormhole package
 */
import { OpalApiComponent } from '@opallabs/api'
import { Collection } from '@opallabs/lambda'
import { DeserializedData } from './deserializedDataTypes'

type MomentResponse = OpalApiComponent<'V2', 'moment'>
type BrandResponse = OpalApiComponent<'V2', 'brand'>
type AssignmentResponse = OpalApiComponent<'V2', 'assignment'>
type PhaseItemResponse = OpalApiComponent<'V2', 'phase_item'>
type StageResponse = OpalApiComponent<'V2', 'stage'>
type StoryResponse = OpalApiComponent<'V2', 'story'>
type AssetReferenceResponse = OpalApiComponent<'V2', 'asset_reference'>
type ContentRespose = OpalApiComponent<'V2', 'content'>
type LabelResponse = OpalApiComponent<'V2', 'label'>

/**
 * For easier availability in other sub-packages
 */
import { SubTab as SubTabType } from './src/actions/createCheckpoints/createCheckpointRequests'
import { MomentPreviewData as MomentPreviewDataType } from './src/selectors/selectors/entities/momentPreviewData'
import {
  NormalizedApproval as NormalizedApprovalInterface,
  NormalizedTask as NormalizedTaskInterface,
} from './src/selectors/selectors/types'
import { Avatar } from './undocumentedTypes/OpalApi/Avatar'
import { User } from './undocumentedTypes/OpalApi/Users'

export type Me = import('./undocumentedTypes/OpalApi/Users').Me

export type SubTab = SubTabType
export type NormalizedApproval = NormalizedApprovalInterface
export type NormalizedTask = NormalizedTaskInterface
export type MomentPreviewData = MomentPreviewDataType

export interface ReferencedObject {
  model_type: string
  text: string
  url: string
}
export interface Notification {
  downloadUrl?: string
  heading?: string
  subHeading?: string
  id: string
  placeholder: boolean
  read: boolean
  referencedObjects: ReferencedObject[]
  seen: boolean
  supplementalText?: string
  timeFormat: string
  timestamp: Date
  type: string
  collaborationThreadId?: string
}

export interface ResponseRelationship {
  id: string
  type: 'response'
}

export type AvatarCollection = Collection<Avatar>

export type Assignment = DeserializedData<AssignmentResponse>

export type AssignmentCollection = Collection<Assignment>

export type Brand = DeserializedData<BrandResponse>

export type BrandCollection = Collection<Brand>

export interface Phase {
  due_date: string | null
  id: string
  name: string
  is_current?: boolean
  is_complete?: boolean
}

export type PhaseCollection = Collection<Phase>

export type PhaseItem = DeserializedData<PhaseItemResponse>

export type PhaseItemCollection = Collection<PhaseItem>

export type Stage = DeserializedData<StageResponse>

export type StageCollection = Collection<Stage>

export type Story = DeserializedData<StoryResponse>

export type StoryCollection = Collection<Story>

export type AssetReference = DeserializedData<AssetReferenceResponse>

export type AssetReferenceCollection = Collection<AssetReference>

export interface StageUpdateParams {
  id: string
  due_date?: string
  metadata?: {
    title?: string
  }
  position?: number
}

export type Content = DeserializedData<ContentRespose>

export type ContentCollection = Collection<Content>

export interface Workflow {
  id: string
  relationships: {
    context: {
      id: string
    }
  }
}

export type WorkflowCollection = Collection<Workflow>

export interface Context {
  id: string
  relationships: {
    moment: {
      id: string
    }
  }
}

export type ContextCollection = Collection<Context>

type Label = DeserializedData<LabelResponse>
type LabelCollection = Collection<Label>

export type Moment = DeserializedData<MomentResponse>

export type MomentCollection = Collection<Moment>

export interface Service {
  color: string
  has_placements: boolean
  icon_name: string
  id: string
  is_active: boolean
  is_visible: boolean
  name: string
  system_name: string
  type: 'service'
  web_modules_channel: boolean
  asset_reference?: {
    attributes: {
      uuid: string
    }
  }
}

export interface Response {
  id: string
  type: 'response'
  created_at: string
  updated_at: string
  deleted_at: string | null
  details: {
    complete?: boolean
    approved: 'approved' | 'declined'
    reasoning: string | null
  }
  relationships: {
    assignment: {
      id: string
    }
  }
}

export type ResponseCollection = Collection<Response>

export type ServiceCollection = Collection<Service>
export type UserCollection = Collection<User>

export interface NormalizedAssignmentContext {
  accountId: string
  id: string
  momentId: string | null
  name: string
  type: 'moment' | 'content'
  status: string
}

export interface NormalizedAssignment {
  assigneeId: string
  context: NormalizedAssignmentContext
  dueDate: string
  id: string
  name: string
  originalId: string
  scheduledAt: string | null
  service: Service | null
  type: string | null
}

export enum StateKeys {
  avatars = 'avatars',
  assignment = 'assignment',
  asset_reference = 'asset_reference',
  brand = 'brand',
  content = 'content',
  context = 'context',
  entityDataByView = 'entityDataByView',
  jsonApiEntities = 'jsonApiEntities',
  moment = 'moment',
  personalPanel = 'personalPanel',
  phase = 'phase',
  phase_item = 'phase_item',
  response = 'response',
  service = 'service',
  stage = 'stage',
  users = 'users',
  workflow = 'workflow',
}

export type StatusObject = {
  unseen: number
}

export type NonTaskTabStatusKeys =
  | 'announcements'
  | 'downloads'
  | 'notifications'
export type TaskTabStatusKeys = 'tasks' | 'approvals'

export type TabStatusKeys = TaskTabStatusKeys | NonTaskTabStatusKeys

export type NonTaskTabStatuses = {
  [key in NonTaskTabStatusKeys]: StatusObject
}

export type TaskTabStatuses = {
  tasks: StatusObject & {
    todo: StatusObject
    upcoming: StatusObject
    completed: StatusObject
    overdue: StatusObject
  }
  approvals: StatusObject & {
    pending: StatusObject
    upcoming: StatusObject
    completed: StatusObject
    overdue: StatusObject
  }
}

export type Status = TaskTabStatuses & NonTaskTabStatuses & StatusObject

export type SelectedFilter = { id: string; disabled: boolean }
export type SelectedFilters = {
  account?: Array<SelectedFilter>
  service?: Array<SelectedFilter>
  labels?: Record<number, Array<SelectedFilter>>
  status?: Array<SelectedFilter>
}

export type FilterTreeNode = {
  id: string
  title: string
  icon: string
  color?: string
  uuid?: string
  payload?: {
    type_key: string
    object: unknown
  }
  childOptions?: Array<FilterTreeNode>
}

export interface State {
  jsonApiEntities?: {
    avatars?: AvatarCollection
    assignment?: AssignmentCollection
    asset_reference?: AssetReferenceCollection
    brand?: BrandCollection
    content?: ContentCollection
    context?: ContextCollection
    label?: LabelCollection
    moment?: MomentCollection
    phase?: PhaseCollection
    phase_item?: PhaseItemCollection
    response?: ResponseCollection
    service?: ServiceCollection
    stage?: StageCollection
    story?: StoryCollection
    workflow?: WorkflowCollection
    meta?: Record<string, any>
    users?: UserCollection
  }
  current_opal_id: string
  current_workspace_id: string
  filters: {
    filterTree?: Array<FilterTreeNode>
    value?: Array<string>
    selectedFilters?: SelectedFilters
    isTrayOpen: boolean
  }
  entityDataByView: {
    assignmentsView?: Record<string, any>
    personalPanel?: Record<string, any>
    storiesView?: Record<string, any>
    weekView?: Record<string, any>
  }
  me?: Me
  status?: Status
}
